import React from "react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { MapPin } from "lucide-react";

export const DetailsHeader = ({ routeDetails }: { routeDetails: any }) => {
  const route = routeDetails;

  const routeId = route?.routeNumber || route?.id || "RT-0001";
  const routeName = route?.routeName || "North Residential Zone A";
  const activeStatus = route?.isActive !== false ? "Active" : "Inactive";
  const totalStops = route?.premiseCount ?? route?.totalPremises ?? 0;
  const assignedTo = route?.meterCount ?? "NA";

  const overviewItems = [
    {
      label: "Premises",
      value: Array.isArray(route?.premiseName)
        ? route.premiseName.join(", ")
        : "NA",
    },
    {
      label: "Area",
      value: Array.isArray(route?.areaName)
        ? route.areaName.join(", ")
        : route?.areaName || "NA",
    },
    {
      label: "Read Cycle",
      value: route?.readCycleName || route?.readCycleDisplay || "NA",
    },
    {
      label: "Last Read Date",
      value: route?.lastReadDate || route?.nextRunDate || "NA",
    },
  ];

  const auditItems = [
    {
      label: "Created By",
      value: route?.createdUserRemoteName || "System Admin",
    },
    {
      label: "Created On",
      value: route?.createdDate || "NA",
    },
    {
      label: "Last Updated By",
      value: route?.lastModifiedUserRemoteName || "—",
    },
    {
      label: "Last Updated On",
      value: route?.lastModifiedDate || "NA",
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200/80 p-6 shadow-xs space-y-6">
      {/* ---- Header Section ---- */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        {/* Left Info */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
            <MapPin className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
              {routeName}
            </h2>
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              {route?.readCycleName && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                  {route.readCycleName}
                </span>
              )}
              {Array.isArray(route?.zoneName) ? (
                route.zoneName.map((z: string, idx: number) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                  >
                    {z}
                  </span>
                ))
              ) : route?.zoneName ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {route.zoneName}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right Stat Box */}
        <div className="border border-gray-200/90 rounded-xl flex items-center divide-x divide-gray-200/90 bg-white overflow-hidden self-start sm:self-auto shrink-0 shadow-2xs">
          <div className="px-5 py-3 text-left flex flex-col justify-center min-w-[95px]">
            <span className="text-xs text-gray-400 font-normal mb-1.5">
              Status
            </span>
            <div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeStatus === "Active"
                    ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                    : "bg-gray-100 text-gray-600 border border-gray-200"
                }`}
              >
                {activeStatus}
              </span>
            </div>
          </div>
          <div className="px-6 py-3 text-left flex flex-col justify-center min-w-[85px]">
            <span className="text-xs text-gray-400 font-normal mb-1.5">
              Total Premises
            </span>
            <span className="text-xl font-bold text-gray-900">
              {totalStops}
            </span>
          </div>
          <div className="px-6 py-3 text-left flex flex-col justify-center min-w-[110px]">
            <span className="text-xs text-gray-400 font-normal mb-1.5">
              Total Resources
            </span>
            <span className="text-sm font-bold text-gray-900 truncate max-w-[140px]">
              {assignedTo}
            </span>
          </div>
        </div>
      </div>

      {/* ---- Route Overview Section ---- */}
      <div className="pt-2 border-t border-gray-100">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Route Overview</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {overviewItems.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>
      </div>

      {/* ---- Audit Section ---- */}
      <div className="pt-2 border-t border-gray-100">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Audit</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {auditItems.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
export default DetailsHeader;
